import type { LinkedItemsSummary } from '@/lib/api/projects.api'
import type { ProjectFilters } from '@/store/project/types'
import type { UseQueryResult } from '@tanstack/react-query'
import { getErrorStatus } from '@/lib/api/error-utils'
import { projectsApi } from '@/lib/api/projects.api'
import {
  isProjectQueryInScope,
  scopedProjectKey,
} from '@/lib/realtime/project-query-scope'
import { useQuery } from '@tanstack/react-query'

import type { ProjectWithEntity } from '@scrambl/shared'
import { ModuleTypeEnum } from '@scrambl/shared'

import { queryKeys } from '../query-keys'
import { useWorkspaceSession } from '../useWorkspaceSession'

// A denied refresh must not leave the formerly readable snapshot on screen.
function projectAccessResult<T>(query: UseQueryResult<T>) {
  const denied = [401, 403, 404].includes(getErrorStatus(query.error) ?? 0)
  return { ...query, data: denied ? undefined : query.data }
}

interface ProjectsResponse {
  data: ProjectWithEntity[]
  meta: {
    total: number
  }
}

const normalizeStringArrayFilter = (value: unknown): string[] => {
  const values = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? [value]
      : []

  return Array.from(
    new Set(
      values
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim())
        .filter(Boolean),
    ),
  )
}

/**
 * Hook to fetch projects with filters
 * Filters are stored in Zustand, but the data comes from TanStack Query
 */
export function useProjectsQuery(
  filters?: ProjectFilters,
  options?: {
    enabled?: boolean
  },
) {
  const scope = useWorkspaceSession()

  const query = useQuery<ProjectsResponse>({
    queryKey: scopedProjectKey(
      queryKeys.projects.list(filters as Record<string, unknown>),
      scope,
    ),
    queryFn: ({ signal }) =>
      scope.run(signal, async signal => {
        // Merge filters with defaults (allow override of includeCustomFields)
        const requestFilters: ProjectFilters = {
          includeCustomFields: filters?.includeCustomFields ?? true, // Default to true, but allow override
          ...filters,
        }
        const entityColumnFilterIds = normalizeStringArrayFilter(
          requestFilters.columnFilters?.entityId,
        )

        if (entityColumnFilterIds.length > 0) {
          delete requestFilters.entityId
          if (requestFilters.moduleType === ModuleTypeEnum.ENTITY) {
            delete requestFilters.moduleType
            delete requestFilters.moduleId
          }
        }

        return projectsApi.getProjects(requestFilters, { signal })
      }),
    enabled: options?.enabled !== false && scope.enabled,
    placeholderData: (previous, query) =>
      query && isProjectQueryInScope(query.queryKey, scope)
        ? previous
        : undefined,
    staleTime: 60 * 1000, // 1 minute - projects can change
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  })
  return projectAccessResult(query)
}

/**
 * Hook to fetch a single project by ID
 */
export function useProjectQuery(
  id: string | undefined,
  options?: {
    includeCustomFields?: boolean
    enabled?: boolean
  },
) {
  const scope = useWorkspaceSession()

  const query = useQuery<ProjectWithEntity>({
    queryKey: scopedProjectKey(
      queryKeys.projects.detail(id || '', {
        includeCustomFields: options?.includeCustomFields !== false,
      }),
      scope,
    ),
    queryFn: ({ signal }) =>
      scope.run(signal, signal =>
        projectsApi.getProject(id!, {
          includeCustomFields: options?.includeCustomFields !== false,
          signal,
        }),
      ),
    enabled: options?.enabled !== false && !!id && scope.enabled,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  return projectAccessResult(query)
}

/**
 * Hook to fetch projects for a specific entity
 */
/**
 * Hook to fetch linked items summary for a project (used by delete dialog)
 */
export function useProjectLinkedItemsSummaryQuery(
  projectId: string | undefined,
  options?: {
    enabled?: boolean
  },
) {
  const scope = useWorkspaceSession()
  const query = useQuery<LinkedItemsSummary>({
    queryKey: scopedProjectKey(
      [...queryKeys.projects.detail(projectId || ''), 'linked-items-summary'],
      scope,
    ),
    queryFn: ({ signal }) =>
      scope.run(signal, signal =>
        projectsApi.getLinkedItemsSummary(projectId!, { signal }),
      ),
    enabled: options?.enabled !== false && !!projectId && scope.enabled,
    staleTime: 0, // Always refetch when dialog opens
  })
  return projectAccessResult(query)
}

export function useProjectsByEntityQuery(
  entityId: string | undefined,
  filters?: {
    status?: string
    limit?: number
    enabled?: boolean
  },
) {
  const scope = useWorkspaceSession()

  const query = useQuery<ProjectsResponse>({
    queryKey: scopedProjectKey(
      queryKeys.projects.list({ ...filters, entityId }),
      scope,
    ),
    queryFn: ({ signal }) =>
      scope.run(signal, signal =>
        projectsApi.getProjectsByEntityId(
          entityId!,
          {
            status: filters?.status,
            limit: filters?.limit,
          },
          { signal },
        ),
      ),
    enabled: filters?.enabled !== false && !!entityId && scope.enabled,
    placeholderData: (previous, query) =>
      query && isProjectQueryInScope(query.queryKey, scope)
        ? previous
        : undefined,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  return projectAccessResult(query)
}

/**
 * Generic polymorphic variant — fetch projects whose moduleType+moduleId point
 * at any parent (e.g. a custom collection entry, a deal, a lead). Mirrors
 * useProjectsByEntityQuery so the same caching/refetch semantics apply.
 */
export function useProjectsByModuleQuery(
  moduleType: string | undefined,
  moduleId: string | undefined,
  filters?: {
    status?: string
    limit?: number
    enabled?: boolean
  },
) {
  const scope = useWorkspaceSession()

  const query = useQuery<ProjectsResponse>({
    queryKey: scopedProjectKey(
      queryKeys.projects.list({ ...filters, moduleType, moduleId }),
      scope,
    ),
    queryFn: ({ signal }) =>
      scope.run(signal, signal =>
        projectsApi.getProjectsByModule(
          moduleType!,
          moduleId!,
          {
            status: filters?.status,
            limit: filters?.limit,
          },
          { signal },
        ),
      ),
    enabled:
      filters?.enabled !== false && !!moduleType && !!moduleId && scope.enabled,
    placeholderData: (previous, query) =>
      query && isProjectQueryInScope(query.queryKey, scope)
        ? previous
        : undefined,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
  return projectAccessResult(query)
}

/**
 * useProjectsQuery under a non-hook name for useModuleTable configs: passing a
 * use*-named import as a value makes React Compiler bail out of the consuming
 * component. Same function, only ever called unconditionally inside the hook.
 */
export const projectsListQuery = useProjectsQuery
